/**
 * Brand-aware primers and odd/invalid flags for staged identifier review.
 * Keep aligned with `src/utils/identifierReviewNotes.ts`.
 */

import { isSiblingCanonItemCode } from './catalogIdentity';
import { isFujifilmNumericCatalogCode } from './fujifilmProductCodes';
import { isNikonBrand, looksLikeNikonUrlSlugSku } from './nikonProductCodes';
import { isSyntheticSlugSku } from './syntheticSku';
import { brandIdentifierPolicy } from './brandIdentifierPolicy';
import { gtin14FromBarcode, isValidGs1 } from './gs1CheckDigit';

export type IdentifierReviewField = 'sku' | 'mpn' | 'upc' | 'ean' | 'gtin';
export type ReviewPrimerField =
  | IdentifierReviewField
  | 'brand'
  | 'model'
  | 'category'
  | 'productType'
  | 'status';
export type IdentifierNoteTone = 'info' | 'odd' | 'invalid';

export interface AcknowledgedReviewFlag {
  by: string;
  at: Date | string;
  value: string;
}

export interface IdentifierFieldNote {
  field: IdentifierReviewField;
  flagId?: string;
  tone: IdentifierNoteTone;
  chip?: string;
  message: string;
  suggestion?: string;
  /** Odd/invalid notes block approve until edited or dismissed for this value. */
  blocks: boolean;
}

export interface IdentifierReviewInput {
  brand?: string | null;
  brandId?: string | null;
  sku?: string | null;
  mpn?: string | null;
  upc?: string | null;
  ean?: string | null;
  gtin?: string | null;
  sourceUrl?: string | null;
  skuSource?: string | null;
  acknowledgedReviewFlags?: Record<string, AcknowledgedReviewFlag> | null;
}

const VARIANT_SUFFIX_SKU = /^(\d{4,})-(\d{1,3})$/;
const COMBO_OFFER_SKU = /^(\d{6,})-(\d{6,})$/;

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

function trimId(value: unknown): string {
  return String(value || '').trim();
}

function isZeroPaddedUpc(ean: string, upc: string): boolean {
  const e = digitsOnly(ean);
  const u = digitsOnly(upc);
  return e.length === 13 && u.length === 12 && e === `0${u}`;
}

/**
 * True when EAN/GTIN is the GS1 pairing of a 12-digit UPC-A (EAN = 0 + UPC,
 * a copied 12-digit UPC, or GTIN = UPC padded to 14) and that UPC passes the
 * check digit. A brand that keeps a separate regional EAN/JAN (Nikon) is
 * never this, even when someone zero-padded the US UPC.
 *
 * ean-as-upc brands often store the published EAN-13 in `upc` (SmallRig);
 * that is not a 12-digit UPC-A, so it stays false. A real 12-digit UPC-A on
 * the same kind of brand (Ilford US boxes) does pair, and Motzu fills it.
 * Keep in sync with src/utils/identifierReviewNotes.ts.
 */
export function isUpcBasedDerivedField(
  field: 'ean' | 'gtin',
  input: Pick<IdentifierReviewInput, 'upc' | 'ean' | 'gtin' | 'brandId' | 'brand'>,
): boolean {
  const policy = brandIdentifierPolicy(input.brandId, input.brand);
  if (policy.independentRegionalEan) return false;

  const upc = digitsOnly(trimId(input.upc));
  if (upc.length !== 12 || !isValidGs1(upc)) return false;

  if (field === 'ean') {
    const ean = digitsOnly(trimId(input.ean));
    if (!ean) return false;
    return ean === upc || ean === `0${upc}`;
  }

  const gtin = digitsOnly(trimId(input.gtin));
  return gtin.length === 14 && gtin === upc.padStart(14, '0');
}

function isLikelyJapanJan(ean: string): boolean {
  const e = digitsOnly(ean);
  return e.length === 13 && (e.startsWith('45') || e.startsWith('49'));
}

export function identifierAreaPrimers(
  brandId?: string | null,
  brand?: string | null,
): Record<ReviewPrimerField, string> {
  const policy = brandIdentifierPolicy(brandId, brand);
  const skuExample = policy.skuExample ? ` Example: ${policy.skuExample}.` : '';
  const gs1 = policy.gs1Notes ? ` ${policy.gs1Notes}` : '';
  return {
    sku: `SKU is the vendor catalog / stock-keeping code. ${policy.skuLooksLike}${skuExample}`,
    mpn: policy.mpnRule
      ? `MPN is the manufacturer part number. ${policy.mpnRule}`
      : 'MPN is the manufacturer part number. Often the same as SKU; Canon kit vs body item codes can differ and still be correct.',
    upc: `UPC is the barcode used at US/Canada POS. ${policy.upcLooksLike}${gs1}`,
    ean: `EAN is the 13-digit international barcode. ${policy.eanLooksLike}${gs1}`,
    gtin: policy.gtinLooksLike,
    brand: 'Manufacturer name from the master brand list (Sony, Canon, Nikon). Must match the scrape brand.',
    model: 'Marketing / catalog model name — not the SKU or a URL slug.',
    category: 'Leaf taxonomy slug (mirrorless, lens-zoom, tripod, …).',
    productType: 'High-level type: camera, lens, or accessory.',
    status: 'Lifecycle from the brand site: active, discontinued, or coming soon.',
  };
}

export function identifierFieldValue(
  input: IdentifierReviewInput,
  field: IdentifierReviewField,
): string {
  return trimId(input[field]);
}

/** Binding stored on an acknowledgement so cross-field flags reopen when a dependency changes. */
export function identifierFlagBinding(
  input: IdentifierReviewInput,
  flagId: string,
  field: IdentifierReviewField = flagId.split('.')[0] as IdentifierReviewField,
): string {
  const fieldValue = identifierFieldValue(input, field);
  switch (flagId) {
    case 'ean.unexpected_mismatch':
      return [fieldValue, trimId(input.upc), trimId(input.brandId), trimId(input.brand)].join('\u241f');
    case 'gtin.mismatch':
    case 'gtin.length':
      return [fieldValue, trimId(input.upc), trimId(input.ean)].join('\u241f');
    case 'mpn.fujifilm_mismatch':
    case 'mpn.fujifilm_catalog':
      return [fieldValue, trimId(input.sku)].join('\u241f');
    default:
      return fieldValue;
  }
}

export function isIdentifierFlagAcknowledged(
  flagId: string,
  input: IdentifierReviewInput,
  acks?: Record<string, AcknowledgedReviewFlag> | null,
): boolean {
  const ack = acks?.[flagId];
  if (!ack) return false;
  const field = flagId.split('.')[0] as IdentifierReviewField;
  if (!['sku', 'mpn', 'upc', 'ean', 'gtin'].includes(field)) return false;
  return trimId(ack.value) === identifierFlagBinding(input, flagId, field);
}

function note(partial: Omit<IdentifierFieldNote, 'blocks'>): IdentifierFieldNote {
  const blocks = partial.tone === 'odd' || partial.tone === 'invalid';
  return { ...partial, blocks };
}

function annotateSku(input: IdentifierReviewInput): IdentifierFieldNote | undefined {
  const sku = trimId(input.sku);
  if (!sku) return undefined;
  const policy = brandIdentifierPolicy(input.brandId, input.brand);

  const variant = sku.match(VARIANT_SUFFIX_SKU);
  if (variant) {
    return note({
      field: 'sku',
      flagId: 'sku.variant_suffix',
      tone: 'invalid',
      chip: 'Not a catalog SKU',
      message: `${sku} looks like a Shopify / variant suffix, not a catalog SKU. Use ${variant[1]}.`,
      suggestion: variant[1],
    });
  }

  const combo = sku.match(COMBO_OFFER_SKU);
  if (combo) {
    return note({
      field: 'sku',
      flagId: 'sku.combo_offer',
      tone: 'invalid',
      chip: 'Combo offer SKU',
      message: `${sku} looks like a hyphenated kit/offer SKU. Use the body catalog code ${combo[1]}.`,
      suggestion: combo[1],
    });
  }

  if (policy.fujifilmCatalog && !isFujifilmNumericCatalogCode(sku)) {
    return note({
      field: 'sku',
      flagId: 'sku.fujifilm_catalog',
      tone: 'invalid',
      chip: 'Fujifilm catalog',
      message: 'Fujifilm SKU must be a numeric catalog code with at least 8 digits (not a lens string).',
    });
  }

  // Nikon only: other regional-pair brands (Manfrotto, Gitzo, Lowepro) sell
  // real letter model codes that the slug pattern would wrongly catch.
  if (isNikonBrand(input.brand, input.brandId) && looksLikeNikonUrlSlugSku(sku)) {
    return note({
      field: 'sku',
      flagId: 'sku.nikon_slug',
      tone: 'invalid',
      chip: 'URL slug',
      message: 'This looks like a Nikon URL slug (NIKKOR…), not the numeric storefront catalog id.',
    });
  }

  if (isSyntheticSlugSku(sku, input.sourceUrl || undefined, input.skuSource || undefined)) {
    return note({
      field: 'sku',
      flagId: 'sku.synthetic_slug',
      tone: 'odd',
      chip: 'Handle SKU',
      message: 'SKU is the storefront URL handle, not a vendor part number. Replace it with the catalog SKU.',
    });
  }

  return undefined;
}

function annotateMpn(input: IdentifierReviewInput): IdentifierFieldNote | undefined {
  const sku = trimId(input.sku);
  const mpn = trimId(input.mpn);
  if (!mpn) {
    const policy = brandIdentifierPolicy(input.brandId, input.brand);
    if (policy.fujifilmCatalog && isFujifilmNumericCatalogCode(sku)) {
      return note({
        field: 'mpn',
        flagId: 'mpn.fujifilm_catalog',
        tone: 'invalid',
        chip: 'Fujifilm catalog',
        message: 'Fujifilm MPN should be the same numeric catalog code as SKU.',
        suggestion: sku,
      });
    }
    return undefined;
  }

  const policy = brandIdentifierPolicy(input.brandId, input.brand);
  if (policy.fujifilmCatalog) {
    if (!isFujifilmNumericCatalogCode(mpn)) {
      return note({
        field: 'mpn',
        flagId: 'mpn.fujifilm_catalog',
        tone: 'invalid',
        chip: 'Fujifilm catalog',
        message: 'Fujifilm MPN must be numeric with at least 8 digits (same as SKU).',
        suggestion: isFujifilmNumericCatalogCode(sku) ? sku : undefined,
      });
    }
    if (isFujifilmNumericCatalogCode(sku) && sku !== mpn) {
      return note({
        field: 'mpn',
        flagId: 'mpn.fujifilm_mismatch',
        tone: 'invalid',
        chip: 'SKU ≠ MPN',
        message: 'Fujifilm SKU and MPN must be the same catalog code.',
        suggestion: sku,
      });
    }
  }

  if (sku && sku.toLowerCase() === mpn.toLowerCase()) {
    return note({
      field: 'mpn',
      tone: 'info',
      message: 'MPN matches SKU — normal for most brands.',
    });
  }

  if (isSiblingCanonItemCode(sku, mpn)) {
    return note({
      field: 'mpn',
      tone: 'info',
      message: 'Canon kit vs body retail codes (e.g. 1483C010 vs 1483C002) are different simples — this is normal.',
    });
  }

  return undefined;
}

function annotateUpc(input: IdentifierReviewInput): IdentifierFieldNote | undefined {
  const upc = trimId(input.upc);
  if (!upc) return undefined;
  if (/\D/.test(upc)) {
    return note({
      field: 'upc',
      flagId: 'upc.non_digit',
      tone: 'invalid',
      chip: 'Not digits',
      message: 'UPC must be digits only — letters or punctuation are not a barcode.',
    });
  }
  const digits = digitsOnly(upc);
  const policy = brandIdentifierPolicy(input.brandId, input.brand);

  if (digits.length === 11) {
    const padded = `0${digits}`;
    return note({
      field: 'upc',
      flagId: 'upc.pad_leading_zero',
      tone: 'invalid',
      chip: '11 digits',
      message: 'UPC-A is 12 digits. A missing leading zero is common — pad it if the checksum matches.',
      suggestion: isValidGs1(padded) ? padded : undefined,
    });
  }

  if (policy.useEanAsUpc && digits.length === 13) {
    if (!isValidGs1(digits)) {
      return note({
        field: 'upc',
        flagId: 'upc.checksum',
        tone: 'invalid',
        chip: 'Bad checksum',
        message: 'This 13-digit UPC/EAN fails the GS1 check digit.',
      });
    }
    return note({
      field: 'upc',
      tone: 'info',
      message: '13-digit EAN stored as UPC is normal for this brand (they do not publish a separate UPC-A).',
    });
  }

  if (digits.length === 13 && !policy.useEanAsUpc) {
    const asUpc = digits.startsWith('0') ? digits.slice(1) : undefined;
    return note({
      field: 'upc',
      flagId: 'upc.looks_like_ean',
      tone: 'odd',
      chip: 'Looks like EAN',
      message: 'This is 13 digits — usually an EAN, not a US UPC-A.',
      suggestion: asUpc && isValidGs1(asUpc) ? asUpc : undefined,
    });
  }

  if (digits.length !== 12) {
    return note({
      field: 'upc',
      flagId: 'upc.length',
      tone: 'invalid',
      chip: `${digits.length || upc.length} digits`,
      message: 'UPC-A should be 12 digits (or 13 when this brand stores EAN as UPC).',
    });
  }

  if (!isValidGs1(digits)) {
    return note({
      field: 'upc',
      flagId: 'upc.checksum',
      tone: 'invalid',
      chip: 'Bad checksum',
      message: 'UPC fails the GS1 check digit — confirm it was not truncated or typed wrong.',
    });
  }

  if (digits.startsWith('0')) {
    return note({
      field: 'upc',
      tone: 'info',
      message: 'Leading zero is normal for many US UPC-A codes — do not strip it.',
    });
  }

  return undefined;
}

function annotateEan(input: IdentifierReviewInput): IdentifierFieldNote | undefined {
  const ean = trimId(input.ean);
  const upc = trimId(input.upc);
  if (!ean) return undefined;
  if (/\D/.test(ean)) {
    return note({
      field: 'ean',
      flagId: 'ean.non_digit',
      tone: 'invalid',
      chip: 'Not digits',
      message: 'EAN must be digits only — letters or punctuation are not a barcode.',
    });
  }
  const digits = digitsOnly(ean);
  const policy = brandIdentifierPolicy(input.brandId, input.brand);

  if (digits.length !== 13) {
    const upcDigits = digitsOnly(upc);
    const copiedUpc = upcDigits.length === 12 && digits === upcDigits;
    // Only UPC-pairing brands treat 0+UPC as the correct EAN; regional-pair /
    // ean-as-upc brands must not get that suggestion.
    const suggestPad =
      copiedUpc && !policy.useEanAsUpc && !policy.independentRegionalEan;
    const padded = suggestPad ? `0${upcDigits}` : undefined;
    return note({
      field: 'ean',
      flagId: 'ean.length',
      tone: 'invalid',
      chip: `${digits.length || ean.length} digits`,
      message: suggestPad
        ? 'This is the 12-digit UPC copied into EAN. For this brand EAN-13 is 0 + UPC — pad it, do not copy.'
        : 'EAN should be 13 digits.',
      suggestion: padded && isValidGs1(padded) ? padded : undefined,
    });
  }

  if (!isValidGs1(digits)) {
    return note({
      field: 'ean',
      flagId: 'ean.checksum',
      tone: 'invalid',
      chip: 'Bad checksum',
      message: 'EAN fails the GS1 check digit — confirm it was not truncated or typed wrong.',
    });
  }

  if (upc && isZeroPaddedUpc(ean, upc)) {
    return note({
      field: 'ean',
      tone: 'info',
      message: 'EAN is the UPC with a leading zero — the usual GS1 pairing, not a second product.',
    });
  }

  if (upc && digitsOnly(upc) !== digits) {
    if (policy.independentRegionalEan) {
      return note({
        field: 'ean',
        tone: 'info',
        message: policy.useEanAsUpc
          ? 'Separate EAN next to UPC.'
          : `Different from the US UPC${isLikelyJapanJan(ean) ? ' — Japan JAN (45…/49…)' : ''}. Normal for this brand; do not overwrite the UPC.`,
      });
    }
    return note({
      field: 'ean',
      flagId: 'ean.unexpected_mismatch',
      tone: 'odd',
      chip: 'UPC ≠ EAN',
      message:
        'EAN is a different GS1 number than the UPC, and this brand usually uses 0 + UPC as the EAN. Confirm it is a real regional code.',
    });
  }

  return undefined;
}

function annotateGtin(input: IdentifierReviewInput): IdentifierFieldNote | undefined {
  const gtin = trimId(input.gtin);
  const upc = trimId(input.upc);
  const ean = trimId(input.ean);
  const expected = gtin14FromBarcode(ean || upc);

  if (!gtin) {
    if (expected) {
      return note({
        field: 'gtin',
        tone: 'info',
        message: 'GTIN can be the UPC/EAN padded to 14 digits with leading zeros.',
        suggestion: expected,
      });
    }
    return undefined;
  }

  if (/\D/.test(gtin)) {
    return note({
      field: 'gtin',
      flagId: 'gtin.non_digit',
      tone: 'invalid',
      chip: 'Not digits',
      message: 'GTIN must be digits only — letters or punctuation are not a barcode.',
    });
  }

  const digits = digitsOnly(gtin);
  if (digits.length !== 14) {
    return note({
      field: 'gtin',
      flagId: 'gtin.length',
      tone: 'odd',
      chip: `${digits.length || gtin.length} digits`,
      message: 'GTIN should be 14 digits. Leading zeros pad UPC/EAN — do not strip them.',
      suggestion: expected,
    });
  }

  if (!isValidGs1(digits)) {
    return note({
      field: 'gtin',
      flagId: 'gtin.checksum',
      tone: 'invalid',
      chip: 'Bad checksum',
      message: 'GTIN fails the GS1 check digit.',
      suggestion: expected,
    });
  }

  if (expected && digits === expected) {
    return note({
      field: 'gtin',
      tone: 'info',
      message: 'Leading zeros are GS1 padding so the UPC/EAN fits GTIN-14. Do not strip them.',
    });
  }

  if (expected && digits !== expected) {
    return note({
      field: 'gtin',
      flagId: 'gtin.mismatch',
      tone: 'odd',
      chip: 'Not padded UPC/EAN',
      message: 'GTIN is not the zero-padded UPC/EAN. Confirm it is the intended GS1 number.',
      suggestion: expected,
    });
  }

  return undefined;
}

/** Value-specific notes (one per field). Primers live on `identifierAreaPrimers`. */
export function annotateIdentifiers(input: IdentifierReviewInput): IdentifierFieldNote[] {
  return [annotateSku, annotateMpn, annotateUpc, annotateEan, annotateGtin]
    .map((fn) => fn(input))
    .filter((n): n is IdentifierFieldNote => Boolean(n));
}

export function blockingIdentifierNotes(input: IdentifierReviewInput): IdentifierFieldNote[] {
  return annotateIdentifiers(input).filter(
    (n) =>
      n.blocks &&
      n.flagId &&
      !isIdentifierFlagAcknowledged(n.flagId, input, input.acknowledgedReviewFlags),
  );
}

/** Fujifilm catalog gaps already have their own approval id — skip those flag ids here. */
const FUJIFILM_FLAG_PREFIX = /^(sku|mpn)\.fujifilm/;

export function extraIdentifierReviewNotes(input: IdentifierReviewInput): IdentifierFieldNote[] {
  return blockingIdentifierNotes(input).filter((n) => !FUJIFILM_FLAG_PREFIX.test(n.flagId || ''));
}

export function identifierReviewIssueMessages(input: IdentifierReviewInput): string[] {
  return extraIdentifierReviewNotes(input).map((n) => n.message);
}

export function pruneAcknowledgedReviewFlags(
  acks: Record<string, AcknowledgedReviewFlag> | undefined,
  current: Pick<IdentifierReviewInput, IdentifierReviewField>,
): Record<string, AcknowledgedReviewFlag> | undefined {
  if (!acks) return undefined;
  const next: Record<string, AcknowledgedReviewFlag> = {};
  for (const [flagId, ack] of Object.entries(acks)) {
    const field = flagId.split('.')[0] as IdentifierReviewField;
    if (!['sku', 'mpn', 'upc', 'ean', 'gtin'].includes(field)) continue;
    if (trimId(ack?.value) !== identifierFlagBinding(current, flagId, field)) continue;
    next[flagId] = ack;
  }
  return Object.keys(next).length > 0 ? next : undefined;
}
